import type { Root as MdastRoot, TopLevelContent } from 'mdast';
import { gfmStrikethroughToMarkdown } from 'mdast-util-gfm-strikethrough';
import { gfmTableToMarkdown } from 'mdast-util-gfm-table';
import { gfmTaskListItemToMarkdown } from 'mdast-util-gfm-task-list-item';
import {
	defaultHandlers as defaultToMarkdownHandlers,
	type Handle,
	toMarkdown,
} from 'mdast-util-to-markdown';

import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';

import { extractAnnotationRanges } from './extractAnnotationRanges';
import { fromPMHandlers as defaultHandlers } from './handlers/index';
import { reduceUnnecessaryEscaping } from './reduceUnnecessaryEscaping';
import type { FromPMHandlers, FromProseMirrorResult } from './types';
import { getHandler } from './utils';

/**
 * Post-process markdown output to undo the `&#x20;` and `&#x9;` encoding
 * that `mdast-util-to-markdown` applies to leading/trailing whitespace in
 * phrasing contexts. See EDITOR-6558 comment in `fromProseMirror`.
 *
 * The library's `unsafe` configuration only supports *adding* patterns,
 * not removing defaults, so we can't prevent the encoding. Instead we
 * replace the character references back to literal characters after
 * serialisation.
 */
function undoEdgeWhitespaceEncoding(md: string): string {
	return md
		.replace(/&#x20;/gu, ' ')
		.replace(/&#x9;/gu, '\t')
		.replace(/^(\s*(?:[-*+]|\d+\.) \[[ xX]\]) \u200B$/gmu, '$1');
}

/**
 * Convert a ProseMirror document node (or its JSON representation) to a
 * markdown string plus a separate array of annotation ranges.
 *
 * The returned `markdown` is a clean string with no annotation syntax embedded.
 * The `annotations` array contains one entry per annotation mark found in the
 * source document, with character offsets into `markdown` so they can be used
 * directly as CM6 DecorationSet ranges or passed to `applyAnnotationRanges`
 * to reconstruct the PM document on commit.
 *
 * Use `fromProseMirror` for the simpler string-only version (backward
 * compatible). Use this function when you need annotation range data.
 */
export function fromProseMirrorWithAnnotations(
	schema: Schema,
	handlers: FromPMHandlers = defaultHandlers,
	options: { hardBreakAsSpaces?: boolean } = {},
) {
	return function (doc: PMNode | Record<string, unknown>): FromProseMirrorResult {
		let pmDoc: PMNode;
		if ('type' in doc && typeof doc.type === 'string') {
			// It's a JSON representation — hydrate it
			pmDoc = schema.nodeFromJSON(doc);
		} else {
			pmDoc = doc as PMNode;
		}

		const children: TopLevelContent[] = [];

		pmDoc.forEach((child) => {
			const handler = getHandler(handlers, child.type.name);
			if (!handler) {
				// Throw on unsupported top-level nodes. Silently dropping at the top level
				// would mean a document with only unknown blocks would serialize to an empty
				// string with no indication something went wrong — a data-loss footgun.
				// Nested unknown nodes are still silently skipped by `convertBlockChildren`
				// so partial-but-meaningful output is still possible for documents whose
				// structure is mostly known.
				throw new Error(
					`ProseMirror node type "${child.type.name}" is not supported by mdast-util-from-pm. ` +
						`Either add a handler for this node type, or filter it out of the document before serializing.`,
				);
			}
			const result = handler(child);
			if (result) {
				if (Array.isArray(result)) {
					children.push(...(result as TopLevelContent[]));
				} else {
					children.push(result as TopLevelContent);
				}
			}
		});

		const mdast: MdastRoot = { type: 'root', children };

		// EDITOR-6558: `mdast-util-to-markdown` encodes leading/trailing
		// whitespace in phrasing contexts as HTML character references
		// (e.g. `&#x20;`). This is technically correct per CommonMark but
		// undesirable for our use case — it makes the markdown unreadable
		// and breaks round-trip identity in the source view.
		//
		// The library's `unsafe` configuration only supports *adding*
		// patterns, not removing defaults, so we post-process the output
		// to replace the character references back to literal characters.
		const rawMarkdown = toMarkdown(mdast, {
			bullet: '*',
			listItemIndent: 'one',
			fences: true,
			// Keep link serialization explicit and stable. By default,
			// mdast-util-to-markdown emits `<https://example.com>` whenever link
			// text and href match, including inline cards that degrade to plain
			// Markdown links. The markdown-mode pipeline expects resource links so
			// ADF link marks and inline-card fallbacks share the same `[text](url)`
			// shape.
			resourceLink: true,
			// Two spaces are standard Markdown hard-break syntax. This keeps a terminal
			// break before another block visible as whitespace instead of a backslash.
			// Defer to the default handler first: it returns a space where a line ending
			// is unsafe (such as table cells), which must not become a newline. Headings
			// with breaks are emitted as setext headings, where the newline is kept.
			...(options.hardBreakAsSpaces
				? {
						handlers: {
							break: ((node, parent, state, info) => {
								const fallback = defaultToMarkdownHandlers.break(node, parent, state, info);
								if (fallback !== '\\\n') {
									return fallback;
								}
								return parent?.children?.at(-1) === node ? '  ' : '  \n';
							}) satisfies Handle,
						},
					}
				: {}),
			// Both are functions in the ESM version (v1.x) installed in AFM
			extensions: [gfmTableToMarkdown(), gfmTaskListItemToMarkdown(), gfmStrikethroughToMarkdown()],
		});

		const markdown = reduceUnnecessaryEscaping(undoEdgeWhitespaceEncoding(rawMarkdown), mdast);

		// Extract annotation ranges from the source PM document.
		// We do this after serialization so the character offsets we compute
		// are in terms of the *final* markdown string (post-encoding fixup).
		const annotations = extractAnnotationRanges(pmDoc, markdown);

		return { markdown, annotations };
	};
}

/**
 * Convert a ProseMirror document node (or its JSON representation) to a
 * markdown string.
 *
 * This is the backward-compatible API. It returns a plain string.
 * Use `fromProseMirrorWithAnnotations` when you also need annotation ranges.
 */
export function fromProseMirror(
	schema: Schema,
	handlers: FromPMHandlers = defaultHandlers,
	options: { hardBreakAsSpaces?: boolean } = {},
) {
	const withAnnotations = fromProseMirrorWithAnnotations(schema, handlers, options);
	return function (doc: PMNode | Record<string, unknown>): string {
		return withAnnotations(doc).markdown;
	};
}
